import { ExperimentTemplatePreview } from '@experiment-details/types';
import { ChartMenuItem } from '@experiment-management-shared/components/Charts/Chart/types';
import { sortBy } from 'lodash';
import {
  PanelDuplicateActionPayload,
  PanelDuplicateConfig,
  PanelDuplicateInViewActionPayload,
  PanelSections
} from '@experiment-management-shared/types';
import {
  generateEmptySection,
  getSectionWithNewPanel
} from '@experiment-management-shared';
import { useDispatch } from 'react-redux';
import { snackbarTypes } from '@/constants/alertTypes';
import alertsUtil from '@/util/alertsUtil';
import useExpDetailsSelectedTemplate from './useExpDetailsSelectedTemplate';
import useExpTemplateList from './template/useExpTemplateList';
import useExpDetailsRouteParams from './useExpDetailsRouteParams';
import { useQueryClient } from 'react-query';
import { expDetailsTemplateKeys } from './api/exp-details-template-key-factory';
import { loadExpDetailsTemplate } from './api/expDetailsTemplateApi';
import useSaveExpDetailsTemplateMutation from './template/useSaveExpDetailsTemplateMutation';
import { AUTO_GENERATED_OPTION_ID } from '@shared/constants';
import { useCallback, useMemo } from 'react';
import useProject from '@API/project/useProject';
import { TemplateUpdateAction } from '@/features/experiment-details/hooks/useExperimentDetailsTemplateState';

interface DuplicateExperimentDetailsPanelConfig {
  onSectionsChange: (
    sections: PanelSections,
    actionType?: TemplateUpdateAction
  ) => void;
}

const DUPLICATED_PANEL_NAME_POSTFIX = `(copy)`;

const useDuplicateExperimentDetailsPanelConfig = ({
  onSectionsChange
}: DuplicateExperimentDetailsPanelConfig): PanelDuplicateConfig => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { data: project } = useProject();
  const { projectId } = useExpDetailsRouteParams();
  const { data: templateList = [] } = useExpTemplateList();

  const { templateData: currentTemplate } = useExpDetailsSelectedTemplate();

  const preparedTemplates = templateList.filter(
    template =>
      !template.personalView &&
      currentTemplate?.templateId !== template.templateId
  );

  const saveTemplateMutation = useSaveExpDetailsTemplateMutation();

  const getProjectOption = useCallback(
    (template: ExperimentTemplatePreview): ChartMenuItem => ({
      label: template.templateName,
      value: template.templateId ?? AUTO_GENERATED_OPTION_ID
    }),
    []
  );
  const getWorkspaceOption = useCallback(
    (template: ExperimentTemplatePreview): ChartMenuItem => ({
      label: template.templateName,
      value: template.templateId ?? AUTO_GENERATED_OPTION_ID,
      secondary: template.projectName
    }),
    []
  );

  const projectViewList: ChartMenuItem[] = useMemo(
    () =>
      preparedTemplates
        .filter(template => template.projectId === projectId)
        .map(getProjectOption),
    [getProjectOption, preparedTemplates, projectId]
  );
  const workspaceViewList: ChartMenuItem[] = useMemo(
    () =>
      preparedTemplates
        .filter(template => template.projectId !== projectId)
        .map(getWorkspaceOption),
    [getWorkspaceOption, preparedTemplates, projectId]
  );

  const optionList = useMemo(() => {
    return [
      {
        title: 'Project views',
        itemList: sortBy(projectViewList, 'label')
      },
      {
        title: 'workspace views',
        infoText:
          'Browse views from all projects within this workspace. Any changes will be saved to a duplicated view within this project.',
        isExpanded: true,
        itemList: sortBy(workspaceViewList, 'label')
      }
    ];
  }, [projectViewList, workspaceViewList]);

  const duplicatePanel = useCallback(
    async ({
      chartId,
      sectionId,
      value: destinationTemplateId
    }: PanelDuplicateActionPayload) => {
      if (!currentTemplate?.chartTemplate) return;

      const destinationTemplate = await queryClient.fetchQuery(
        expDetailsTemplateKeys.projectTemplate(destinationTemplateId),
        () =>
          loadExpDetailsTemplate({
            templateId: destinationTemplateId,
            projectId
          })
      );

      const duplicatedSection = currentTemplate.chartTemplate.panels.sections.find(
        section => section.id === sectionId
      );
      const duplicatedPanel = duplicatedSection?.panels.find(
        panel => panel.chartId === chartId
      );

      if (!destinationTemplate || !duplicatedSection || !duplicatedPanel)
        return;

      const templateToSave = {
        ...destinationTemplate
      };
      const sectionListFromTemplate =
        templateToSave.chartTemplate.panels.sections || [];
      const sectionFromTemplate = sectionListFromTemplate.find(
        section => section.title === duplicatedSection.title
      );
      const layoutItem = duplicatedSection.layout.find(
        layout => layout.i === duplicatedPanel.chartId
      );
      const destinationSection =
        sectionFromTemplate || generateEmptySection(duplicatedSection.title);
      const sectionWithDuplicatedPanel = await getSectionWithNewPanel(
        destinationSection,
        duplicatedPanel,
        layoutItem
      );
      let sectionsToSave = sectionListFromTemplate.map(section => {
        if (section.id === sectionWithDuplicatedPanel.id) {
          return sectionWithDuplicatedPanel;
        }
        return section;
      });
      if (!sectionFromTemplate) {
        sectionsToSave = [
          ...sectionListFromTemplate,
          sectionWithDuplicatedPanel
        ];
      }
      templateToSave.chartTemplate.panels.sections = sectionsToSave;

      saveTemplateMutation.mutate(
        {
          templateData: templateToSave
        },
        {
          onSuccess: templateData => {
            dispatch(
              alertsUtil.openSnackbarDialog(
                snackbarTypes.SUCCESS_API_RESPONSE,
                `Panel successfully copied to ${templateData.templateName}.`
              )
            );
          }
        }
      );
    },
    [currentTemplate?.chartTemplate?.panels?.sections, dispatch, templateList]
  );

  const duplicatePanelInView = useCallback(
    async (payload: PanelDuplicateInViewActionPayload) => {
      if (!currentTemplate?.chartTemplate) return;

      const sections = currentTemplate.chartTemplate.panels.sections;

      const section = sections.find(
        section => section.id === payload.sectionId
      );

      const panel = section?.panels.find(
        panel => panel.chartId === payload.chartId
      );

      if (!panel || !section) return;

      const layoutItem = section.layout.find(
        layout => layout.i === panel.chartId
      );

      const sectionWithDuplicatedPanel = await getSectionWithNewPanel(
        section,
        {
          ...panel,
          chartName: `${panel.chartName} ${DUPLICATED_PANEL_NAME_POSTFIX}`
        },
        layoutItem
      );

      const sectionsToSave = sections.map(section => {
        if (section.id === sectionWithDuplicatedPanel.id) {
          return sectionWithDuplicatedPanel;
        }
        return section;
      });

      onSectionsChange(sectionsToSave, 'add-panel');

      dispatch(
        alertsUtil.openSnackbarDialog(
          snackbarTypes.SUCCESS_API_RESPONSE,
          'Panel duplicated successfully'
        )
      );
    },
    [currentTemplate?.chartTemplate, dispatch, onSectionsChange]
  );

  return useMemo(
    () => ({
      optionList,
      duplicatePanel,
      duplicatePanelInView,
      disabled: !project.canEdit
    }),
    [duplicatePanel, duplicatePanelInView, optionList, project.canEdit]
  );
};

export default useDuplicateExperimentDetailsPanelConfig;
